/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import ChangePasswordWithMfa from './views/changePassword/ChangePasswordWithMfa.redux';
import MfaVerifyMobileNumber from './views/mfaSetup/MfaVerifyMobileNumber.redux';
import MfaSetMobileNumber from './views/mfaSetup/MfaSetMobileNumber.redux';
import MfaEnabled from './views/mfaSetup/MfaEnabled.redux';

import HealthInfoView from '../views/healthInfo/HealthInfoView';

import HealthEntryOccurrence from '../views/occurrences/HealthEntryOccurrenceController';
import { ExistingObservationView } from '../views/occurrences/ObservationView';
import TrackableView from '../views/occurrences/TrackableView';
import TrackableReminderSettings from '../views/reminders/TrackableReminderSettings';
import TrackableReminderDuration from '../views/reminders/TrackableReminderDuration';
import TrackableReminderFrequency from '../views/reminders/TrackableReminderFrequency';
import TrackableReminderTime from '../views/reminders/TrackableReminderTime';

import HealthEntryAmountAndFrequency from '../views/healthEntry/HealthEntryAmountAndFrequency';
import { HealthEntryAnatomy } from '../views/healthEntry/HealthEntryAnatomy';
import { HealthEntryChange } from '../views/healthEntry/HealthEntryChange';
import { HealthEntryComment, TrackableComment } from '../views/healthEntry/OccurrenceCommentView';
import { HealthEntryCreate } from '../views/healthEntry/HealthEntryCreate';
import HealthEntryCustomFrequency from '../views/healthEntry/HealthEntryCustomFrequency';
import HealthEntryDiagnosis from '../views/healthEntry/HealthEntryDiagnosis';
import HealthEntryGeneVariant from '../views/healthEntry/HealthEntryGeneVariant';
import HealthEntryGeneMutationType from '../views/healthEntry/HealthEntryGeneMutationType';
import HealthEntryGeneDNAChange from '../views/healthEntry/HealthEntryGeneDNAChange';
import HealthEntryGeneProteinChange from '../views/healthEntry/HealthEntryGeneProteinChange';
import HealthEntryGeneExon from '../views/healthEntry/HealthEntryGeneExon';
import HealthEntryGeneZygosity from '../views/healthEntry/HealthEntryGeneZygosity';
import { HealthEntryMedicationUnit } from '../views/healthEntry/HealthEntryMedicationUnit';
import { HealthEntryReaction } from '../views/healthEntry/HealthEntryReaction';
import { HealthEntryStoppingReason } from '../views/healthEntry/HealthEntryStoppingReason';
import HealthEntryValueAmount from '../views/healthEntry/HealthEntryValueAmount';
import { HealthEntryValueDescriptor } from '../views/healthEntry/HealthEntryValueDescriptor';
import { HealthEntryValueUnit } from '../views/healthEntry/HealthEntryValueUnit';
import HealthEntryWhen from '../views/healthEntry/HealthEntryWhen';
import Actions from '../views/Actions';

import ShareCardListView from '../views/shareCards/ShareCardListView';
import ViewDataConnections from './views/dataConnections/ViewDataConnections';
import DataConnectionsSettings from './views/dataConnections/DataConnectionsSettings';
import DestinationLinkRedirect from '../views/app/DestinationLinkRedirect';

import Note from '../views/Note';
import FileView from '../views/file/FileView';
import FileUploadErrorTooBig from '../views/file/FileUploadErrorTooBig';
import MyBackpackSelection from '../views/MyBackpackSelection';
import MyProfile from './views/profile/Profile.redux';
import MyProfileEdit from './views/profile/MyProfileEdit.redux';
import CommunicationOptionsEdit from './views/communicationOptions/CommunicationOptionsEdit';
import DependentProfileCreation from './views/profile/DependentProfileCreation.redux';

import ContactUs from './views/ContactUs.redux';
import ViewContactAttach from '../views/contacts/ViewContactAttach';
import ContactRoutes from '../views/contacts/ContactRoutes';
import ViewContactEditNew from '../views/contacts/ViewContactEditNew';

import ReviewPendingData from '../views/reviewPendingData/ReviewPendingData';

import ConfirmDeleteProfile from './views/ConfirmDeleteProfile';
import Notifications from './views/notifications/Notifications';
import SetPreferredLanguage from '../views/SetPreferredLanguage';
import Groups from './views/groups/Groups';
import GroupJoinInstructions from './views/GroupJoinInstructionsAuthorized.redux';

import DeleteAccountSurvey from './views/DeleteAccountSurvey.redux';
import DeleteAccountConfirm from './views/DeleteAccountConfirm';

import ProfilePhotoCrop from './views/profile/ProfilePhotoCrop.redux';
import ProfilePhotoError from './views/profile/ProfilePhotoError.redux';

import AccountChangeEmail from '../views/AccountChangeEmail';
import ChangePassword from './views/changePassword/ChangePassword.redux';

import RouteNames from './RouteNames';

import ProfileRace from './views/profile/ProfileRace.redux';
import { AccountSettings } from './views/profile/AccountSettings';
import RoutesProfileAccess from './views/profile/ProfileAccess/RoutesProfileAccess';

import DeleteAccountSuccess from './views/DeleteAccountSuccess';
import { useAllMedliConstants } from '../hooks/useMedli';
import { useActiveUser } from '../hooks/useUser';

const RoutesWithValidProfile = () => {
  const [{ preferredLanguageCode }] = useActiveUser();
  useAllMedliConstants(preferredLanguageCode);

  return (
    <Switch>
      <Route exact path={RouteNames.destinationLink()} component={DestinationLinkRedirect} />
      <Route exact path={RouteNames.deleteAccountSuccess()} component={DeleteAccountSuccess} />

      <Route exact path="/home" component={HealthInfoView} />
      <Route path={RouteNames.groups()} component={Groups} />
      <Route path={RouteNames.actions()} component={Actions} />

      <Route exact path="/profile/new" component={DependentProfileCreation} />

      <Route exact path="/profile" component={MyProfile} />
      <Route exact path="/profile/info" component={MyProfile} />
      <Route exact path="/profile/info/race" component={ProfileRace} />
      <Route exact path="/profile/info/:propertyName" component={MyProfileEdit} />
      <Route exact path="/profile/settings" component={MyProfile} />
      <Route exact path="/profile/privacy" component={MyProfile} />
      <Route exact path={RouteNames.setPreferredLanguage()} component={SetPreferredLanguage} />
      <Route exact path={RouteNames.profileDelete()} component={ConfirmDeleteProfile} />
      <Route exact path="/profile/photo/crop" component={ProfilePhotoCrop} />
      <Route exact path="/profile/photo/error/dimension" component={ProfilePhotoError} />
      <Route exact path={RouteNames.accountSettings()} component={AccountSettings} />
      <Route path={RouteNames.profileAccess()} component={RoutesProfileAccess} />

      <Route exact path="/sharecard" component={ShareCardListView} />
      <Route exact path="/sharecard/comment" component={ShareCardListView} />
      <Route exact path="/sharecard/:id" component={ShareCardListView} />
      <Route exact path="/sharecard/:id/comment" component={ShareCardListView} />
      <Route exact path="/sharecard/:id/:definitionId" component={ShareCardListView} />

      <Route exact path="/mybackpack/selection/notes" component={MyBackpackSelection} />
      <Route exact path="/mybackpack/selection/files" component={MyBackpackSelection} />
      <Route exact path="/mybackpack/selection" component={MyBackpackSelection} />
      <Route path={RouteNames.myBackpack()} component={HealthInfoView} />


      <Route exact path={RouteNames.accountSettings()} component={AccountSettings} />
      <Route exact path={RouteNames.deleteAccountConfirm()} component={DeleteAccountConfirm} />
      <Route exact path={RouteNames.deleteAccountSurvey()} component={DeleteAccountSurvey} />

      <Route exact path="/notifications" component={Notifications} />

      <Route exact path={RouteNames.contactUs()} component={ContactUs} />

      <Route exact path={RouteNames.changeEmail()} component={AccountChangeEmail} />
      <Route exact path={RouteNames.changePassword()} component={ChangePassword} />
      <Route exact path="/profile/account/mfa/changePassword" component={ChangePasswordWithMfa} />
      <Route exact path={RouteNames.communicationOptions()} component={CommunicationOptionsEdit} />
      <Route exact path="/profile/account" component={MyProfile} />

      <Route exact path={RouteNames.mfaSetMobileNumber()} component={MfaSetMobileNumber} />
      <Route exact path="/profile/account/mfa/verifyMobileNumber" component={MfaVerifyMobileNumber} />
      <Route exact path="/profile/account/mfa" component={MfaEnabled} />

      <Route exact path="/healthEntry/create/:medliCategory" component={HealthEntryCreate} />

      <Route exact path={RouteNames.contactAttachNew()} component={ViewContactEditNew} />
      <Route exact path={RouteNames.contactAttach()} component={ViewContactAttach} />

      <Route exact path="/healthEntry/:healthEntryId/change/search" component={HealthEntryChange} />
      <Route exact path="/healthEntry/:healthEntryId/change/list" component={HealthEntryChange} />
      <Route exact path="/healthEntry/:healthEntryId/when" component={HealthEntryWhen} />
      <Route exact path="/healthEntry/:healthEntryId/diagnosis" component={HealthEntryDiagnosis} />
      <Route exact path="/healthEntry/:healthEntryId/anatomy" component={HealthEntryAnatomy} />
      <Route exact path="/healthEntry/:healthEntryId/reaction" component={HealthEntryReaction} />
      <Route exact path="/healthEntry/:healthEntryId/stoppingReason" component={HealthEntryStoppingReason} />
      <Route exact path="/healthEntry/:healthEntryId/frequencyUnit/:groupName" component={HealthEntryCustomFrequency} />
      <Route exact path="/healthEntry/:healthEntryId/amountUnit" component={HealthEntryMedicationUnit} />
      <Route exact path="/healthEntry/:healthEntryId/amountAndFrequency" component={HealthEntryAmountAndFrequency} />
      <Route exact path="/healthEntry/:healthEntryId/file/:fileId" component={FileView} />
      <Route exact path={RouteNames.healthEntryComment()} component={HealthEntryComment} />
      <Route exact path={RouteNames.healthEntryValueComment()} component={HealthEntryComment} />

      <Route exact path="/healthEntry/:healthEntryId/new" component={HealthEntryOccurrence} />

      <Route exact path="/healthEntry/:healthEntryId/values/:valueId" component={HealthEntryOccurrence} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/when" component={HealthEntryWhen} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/amount" component={HealthEntryValueAmount} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/unit" component={HealthEntryValueUnit} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/descriptor" component={HealthEntryValueDescriptor} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/gene-variant" component={HealthEntryGeneVariant} />
      <Route exact path={RouteNames.healthEntryValueGeneMutationType()} component={HealthEntryGeneMutationType} />
      <Route exact path={RouteNames.healthEntryValueGeneDNAChange()} component={HealthEntryGeneDNAChange} />
      <Route exact path={RouteNames.healthEntryValueGeneProteinChange()} component={HealthEntryGeneProteinChange} />
      <Route exact path={RouteNames.healthEntryValueGeneExon()} component={HealthEntryGeneExon} />
      <Route exact path={RouteNames.healthEntryValueGeneZygosity()} component={HealthEntryGeneZygosity} />

      <Route exact path={RouteNames.reviewPendingData()} component={ReviewPendingData} />

      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/note/:noteId" component={Note} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId/file/:fileId" component={FileView} />
      <Route exact path="/healthEntry/:healthEntryId/values/:valueId" component={HealthEntryOccurrence} />
      <Route exact path="/healthEntry/:healthEntryId/values" component={HealthEntryOccurrence} />

      <Route exact path="/healthEntry/:healthEntryId" component={HealthEntryOccurrence} />

      <Route path={RouteNames.contactList()} component={ContactRoutes} />

      <Route path={RouteNames.trackableReminderDuration()} component={TrackableReminderDuration} />
      <Route path={RouteNames.trackableReminderTime()} component={TrackableReminderTime} />
      <Route path={RouteNames.trackableReminderFrequency()} component={TrackableReminderFrequency} />
      <Route path={RouteNames.trackableReminderSettings()} component={TrackableReminderSettings} />

      <Route exact path={RouteNames.trackableComment()} component={TrackableComment} />
      <Route exact path={RouteNames.createTrackableComment()} component={TrackableComment} />
      <Route path={RouteNames.trackableObservation()} component={ExistingObservationView} />
      <Route path={RouteNames.trackableObservationList()} component={TrackableView} />

      <Route exact path="/files/error" component={FileUploadErrorTooBig} />
      <Route exact path="/files/:fileId" component={FileView} />

      <Route exact path="/notes/:noteId" component={Note} />

      <Route exact path={RouteNames.dataConnections()} component={ViewDataConnections} />
      <Route exact path={RouteNames.dataConnectionsSettings()} component={DataConnectionsSettings} />

      <Route exact path={RouteNames.joinGroupInstructions()} component={GroupJoinInstructions} />

      <Redirect to={RouteNames.myBackpack()} />
    </Switch>
  );
};

export default RoutesWithValidProfile;
